/**
 * Which kinds of things a Dispatch view asks its team to acknowledge: the view's Requires Acknowledgement list.
 *
 * A view names what becomes an acknowledgeable item for its team: the red dot, the unacknowledged count, the
 * unacknowledged-only toggle. Everything else still renders (a TMS event outside the list is a thread chip) but never
 * asks for attention. The list names taxonomy, never storage: `tms:` refs are the TMS catalog's category and kind, so
 * a kind that moves between subjects keeps every stored list meaning what it meant. These strings are stored data;
 * renaming one is a data migration. Grammar only here, with no catalog knowledge: this package is browser-safe and may
 * not import the TMS contracts, so the workspace config wire checks `tms:` refs against the catalog (`api-types`).
 * Browser-safe: no React, no DOM.
 */

import { z } from "zod";

/** Messages from a channel's alias owner, and the feed watermarks over those channels. The default. */
export const DRIVER_MESSAGES = "driverMessages";

/** Everything the catalog knows, live: kinds added later are included. Accepted, though no view uses it yet. */
export const EVERYTHING = "*";

/**
 * Open tasks pinned to a board other than the viewer's, on the channels of the drivers they lead to.
 *
 * A task pinned to the viewer's own board, or to none, is on the viewer's shelf whatever the list says.
 */
export const OTHER_BOARD_TASKS = "otherBoardTasks";

/** A catalog category or kind name: camelCase, as the TMS catalog spells them. */
const IDENTIFIER = "[a-z][A-Za-z0-9]*";

const TMS_REF = new RegExp(`^tms:(?<category>${IDENTIFIER})(?:\\.(?<kind>${IDENTIFIER}))?$`);

/**
 * One entry of a Requires Acknowledgement list.
 *
 * `tms:<category>` takes every kind in the category, live, and `tms:<category>.<kind>` one kind.
 */
export type RequiresAcknowledgementRef =
  | typeof DRIVER_MESSAGES
  | typeof EVERYTHING
  | typeof OTHER_BOARD_TASKS
  | `tms:${string}`;

/**
 * A view's Requires Acknowledgement list.
 */
export type RequiresAcknowledgement = { include: RequiresAcknowledgementRef[] };

/** What a view asks for when it names no list: driver messages alone, which is the page's behavior before lists. */
export const DEFAULT_REQUIRES_ACKNOWLEDGEMENT: RequiresAcknowledgement = Object.freeze({
  include: [DRIVER_MESSAGES],
}) as RequiresAcknowledgement;

/**
 * A `tms:` ref's category, and its kind when it names one.
 */
export type TmsAcknowledgementRef = { category: string; kind?: string };

/**
 * Splits a `tms:` ref into its category and kind.
 *
 * @returns null for any other ref, or a string that is not a well-formed `tms:` ref.
 */
export function parseTmsAcknowledgementRef(ref: string): TmsAcknowledgementRef | null {
  const groups = TMS_REF.exec(ref)?.groups;
  if (groups?.["category"] === undefined) {
    return null;
  }
  const { category, kind } = groups as { category: string; kind?: string };
  return kind === undefined ? { category } : { category, kind };
}

/**
 * A TMS category a list may name, with the kinds in it that can be acknowledged.
 *
 * Supplied by the caller from the catalog, since this package may not import it.
 */
export type AcknowledgeableTmsCategory = { category: string; kinds: readonly string[] };

/**
 * A catalog key as a person reads it: `sleeperBerth` → "Sleeper berth".
 */
function sentenceCase(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The label a person reads for a ref, else the ref itself.
 */
export function labelForAcknowledgementRef(ref: string): string {
  if (ref === DRIVER_MESSAGES) {
    return "Driver messages";
  }
  if (ref === EVERYTHING) {
    return "Everything";
  }
  if (ref === OTHER_BOARD_TASKS) {
    return "Tasks pinned to other boards";
  }
  const tms = parseTmsAcknowledgementRef(ref);
  if (tms === null) {
    return ref;
  }
  return `${sentenceCase(tms.category)}: ${tms.kind === undefined ? "every kind" : sentenceCase(tms.kind)}`;
}

/**
 * The TMS categories no list may name, whatever their subject.
 *
 * A driver's own acknowledgement of a message or a verification is the reply to something dispatch sent, never
 * something waiting on dispatch.
 */
const NEVER_ACKNOWLEDGED_CATEGORIES: ReadonlySet<string> = new Set(["acknowledgement"]);

/**
 * The categories a list may name: those given, less any {@link NEVER_ACKNOWLEDGED_CATEGORIES}.
 */
function offeredCategories(categories: readonly AcknowledgeableTmsCategory[]): AcknowledgeableTmsCategory[] {
  return categories.filter(({ category }) => !NEVER_ACKNOWLEDGED_CATEGORIES.has(category));
}

/**
 * The `tms:` refs of a list that name no acknowledgeable TMS event: a category or kind `categories` does not hold.
 *
 * Nothing would ever ask for them, so an editor refuses them rather than store a list that silently asks for less than
 * it reads.
 */
export function unacknowledgeableRefs(
  include: readonly string[],
  categories: readonly AcknowledgeableTmsCategory[],
): string[] {
  return include.filter((ref) => {
    const tms = parseTmsAcknowledgementRef(ref);
    if (tms === null) {
      return false;
    }
    const entry = offeredCategories(categories).find((candidate) => candidate.category === tms.category);
    return entry === undefined || (tms.kind !== undefined && !entry.kinds.includes(tms.kind));
  });
}

/**
 * One group of a Requires Acknowledgement picker: a heading and the refs under it, labeled.
 */
export type AcknowledgementRefGroup = { group: string; items: { value: string; label: string }[] };

/** The heading over refs a list holds that name no acknowledgeable TMS event. */
export const UNACKNOWLEDGEABLE_GROUP = "Not acknowledgeable";

/**
 * What a Requires Acknowledgement picker offers, grouped.
 *
 * Driver messages, tasks pinned to other boards, and everything, then each acknowledgeable TMS category by label, as a
 * whole and kind by kind.
 *
 * @param categories - The acknowledgeable categories, from the catalog.
 * @param held - The refs the list holds now: one naming no acknowledgeable event is listed as stored under
 *   {@link UNACKNOWLEDGEABLE_GROUP}, so a stored list never opens with an entry the picker cannot show.
 */
export function acknowledgementRefGroups(
  categories: readonly AcknowledgeableTmsCategory[],
  held: readonly string[] = [],
): AcknowledgementRefGroup[] {
  const item = (ref: string) => ({ value: ref, label: labelForAcknowledgementRef(ref) });
  const unknown = unacknowledgeableRefs(held, categories);
  const sorted = offeredCategories(categories).sort((a, b) =>
    sentenceCase(a.category).localeCompare(sentenceCase(b.category)),
  );
  return [
    { group: "General", items: [item(DRIVER_MESSAGES), item(OTHER_BOARD_TASKS), item(EVERYTHING)] },
    ...sorted.map(({ category, kinds }) => ({
      group: `TMS events: ${sentenceCase(category)}`,
      items: [item(`tms:${category}`), ...kinds.map((kind) => item(`tms:${category}.${kind}`))],
    })),
    ...(unknown.length > 0
      ? [{ group: UNACKNOWLEDGEABLE_GROUP, items: unknown.map((ref) => ({ value: ref, label: ref })) }]
      : []),
  ];
}

/**
 * Whether a string is a well-formed Requires Acknowledgement ref.
 */
export function isRequiresAcknowledgementRef(value: string): value is RequiresAcknowledgementRef {
  return (
    value === DRIVER_MESSAGES ||
    value === EVERYTHING ||
    value === OTHER_BOARD_TASKS ||
    parseTmsAcknowledgementRef(value) !== null
  );
}

export const RequiresAcknowledgementRefSchema = z.string().refine(isRequiresAcknowledgementRef, {
  message: 'Must be "driverMessages", "*", "tms:<category>", or "tms:<category>.<kind>"',
}) as z.ZodType<RequiresAcknowledgementRef>;

/**
 * Builds the list's schema with one unknown-key policy, as the view definition does.
 *
 * @param object - `z.strictObject` or `z.object`, the policy the enclosing view document applies.
 */
export function requiresAcknowledgementSchema(object: typeof z.strictObject): z.ZodType<RequiresAcknowledgement> {
  return object({ include: z.array(RequiresAcknowledgementRefSchema) }) as z.ZodType<RequiresAcknowledgement>;
}

export const RequiresAcknowledgementSchema = requiresAcknowledgementSchema(z.strictObject);

/**
 * Something that may require acknowledgement, as the list reads it.
 *
 * A TMS event's `category` is null for the catalog's reserved key, which only `*` takes. A `task` is one on the
 * viewer's own shelf; an `otherBoardTask` is one pinned to another board.
 */
export type AcknowledgeableSource =
  | { source: "driverMessage" }
  | { source: "tmsEvent"; category: string | null; kind: string }
  | { source: "task" }
  | { source: "otherBoardTask" };

/**
 * Resolves a view's list to the predicate that decides what becomes an acknowledgeable item.
 *
 * An absent list resolves to {@link DEFAULT_REQUIRES_ACKNOWLEDGEMENT}. A ref that does not parse matches nothing,
 * so a list written by a newer release degrades to what this one understands rather than failing the page. A task on
 * the viewer's own shelf always needs acknowledgement, under every list, because someone made it on purpose; one
 * pinned to another board needs it only when the list names {@link OTHER_BOARD_TASKS}.
 */
export function resolveRequiresAcknowledgement(
  config: RequiresAcknowledgement | undefined,
): (item: AcknowledgeableSource) => boolean {
  const include = (config ?? DEFAULT_REQUIRES_ACKNOWLEDGEMENT).include;
  if (include.includes(EVERYTHING)) {
    return () => true;
  }

  const driverMessages = include.includes(DRIVER_MESSAGES);
  const otherBoardTasks = include.includes(OTHER_BOARD_TASKS);
  const categories = new Set<string>();
  const kinds = new Set<string>();
  for (const ref of include) {
    const tms = parseTmsAcknowledgementRef(ref);
    if (tms === null) {
      continue;
    }
    if (tms.kind === undefined) {
      categories.add(tms.category);
    } else {
      kinds.add(`${tms.category}.${tms.kind}`);
    }
  }

  return (item) => {
    if (item.source === "task") {
      return true;
    }
    if (item.source === "otherBoardTask") {
      return otherBoardTasks;
    }
    if (item.source === "driverMessage") {
      return driverMessages;
    }
    if (item.category === null) {
      return false;
    }
    return categories.has(item.category) || kinds.has(`${item.category}.${item.kind}`);
  };
}
